<script setup lang="ts">
import { computed } from 'vue'
import PChart from 'primevue/chart'
import type { ChartData } from 'chart.js'
import { useThemeStore } from '@uiKit/store/themeStore'

interface Props {
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'radar'
  data: ChartData
  title?: string
  height?: string
  chartOptions?: Record<string, unknown>
}

const props = withDefaults(defineProps<Props>(), {
  height: '200px',
})

const themeStore = useThemeStore()

const mergedOptions = computed(() => {
  // We reference themeStore.isDarkMode so this computed property
  // re-runs whenever the theme changes
  const isDark = themeStore.isDarkMode

  const style = getComputedStyle(document.documentElement)
  const textColor =
    style.getPropertyValue('--p-text-color').trim() || (isDark ? '#ffffff' : '#000000')
  const borderColor = style.getPropertyValue('--p-content-border-color').trim() || '#e2e8f0'

  const base: Record<string, unknown> = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: textColor, font: { size: 12 } },
      },
    },
    scales:
      props.type === 'pie' || props.type === 'doughnut'
        ? {}
        : {
            y: {
              beginAtZero: true,
              grid: { color: borderColor },
              ticks: { color: textColor },
            },
            x: {
              grid: { display: false },
              ticks: { color: textColor },
            },
          },
  }

  const override = props.chartOptions ?? {}
  return {
    ...base,
    ...override,
    plugins: { ...(base.plugins as object), ...(override.plugins as object) },
    scales: { ...(base.scales as object), ...(override.scales as object) },
  }
})
</script>

<template>
  <div class="surface-card p-4 border-round surface-border h-full">
    <div v-if="title" class="text-sm font-bold mb-4 uppercase text-color-secondary">
      {{ title }}
    </div>
    <div :style="{ height: height }">
      <PChart :type="type" :data="props.data" :options="mergedOptions" class="h-full" />
    </div>
  </div>
</template>
